/**
 * Préférence « barre latérale compacte », partagée entre l'écran Paramètres et App.
 *
 * Pourquoi un module dédié plutôt qu'un accès direct à localStorage dans chaque composant :
 * la case à cocher de Paramètres écrivait déjà `sidebarCompact` dans `settings_display`, mais
 * personne ne la relisait — Sidebar ne se replie que sur sa prop `isCollapsed`, pilotée par le
 * chevron de son en-tête. Cocher la case, enregistrer et recharger ne produisait donc aucun
 * effet. Trois composants doivent maintenant s'accorder sur la même valeur, et la clé est
 * imbriquée dans un objet JSON partagé avec le thème : une lecture recopiée trois fois aurait
 * divergé au premier changement de format.
 *
 * La valeur reste dans `settings_display` et non dans une clé à part : c'est là que l'écran
 * Paramètres l'enregistre déjà, et déplacer la clé aurait fait perdre son réglage à quiconque
 * avait coché la case avant ce correctif.
 */

const CLE_STOCKAGE = 'settings_display';

/**
 * Émis quand la préférence change dans l'onglet courant.
 *
 * L'événement natif `storage` ne suffit pas : il n'est notifié qu'aux AUTRES onglets, jamais à
 * celui qui écrit. Sans cet événement maison, enregistrer depuis Paramètres ne replierait la
 * barre qu'après un rechargement — exactement le défaut que ce lot corrige.
 */
export const EVENEMENT_SIDEBAR_COMPACT = 'smartgreen:sidebar-compact';

/** Lit la préférence enregistrée. Toute anomalie de lecture vaut « non compacte ». */
export const lireSidebarCompact = (): boolean => {
    try {
        const brut = localStorage.getItem(CLE_STOCKAGE);
        if (!brut) return false;
        return JSON.parse(brut)?.sidebarCompact === true;
    } catch {
        // localStorage indisponible (navigation privée stricte) ou JSON corrompu : la barre
        // s'affiche dépliée. Un réglage d'affichage illisible ne doit pas empêcher l'application
        // de démarrer.
        return false;
    }
};

/**
 * Écrit la préférence sans toucher aux autres réglages d'affichage, puis notifie l'onglet.
 *
 * La relecture avant écriture est nécessaire : `settings_display` porte aussi le thème et la
 * pagination. Écrire un objet complet depuis le seul appelant qui connaît la compacité
 * écraserait les autres champs.
 */
export const ecrireSidebarCompact = (compact: boolean): void => {
    try {
        const brut = localStorage.getItem(CLE_STOCKAGE);
        const reglages = brut ? JSON.parse(brut) : {};
        localStorage.setItem(CLE_STOCKAGE, JSON.stringify({...reglages, sidebarCompact: compact}));
    } catch {
        // Écriture impossible : la préférence ne survivra pas au rechargement, mais le repli
        // demandé doit tout de même s'appliquer tout de suite. On notifie donc quand même.
    }
    window.dispatchEvent(new CustomEvent(EVENEMENT_SIDEBAR_COMPACT, {detail: compact}));
};
